import { AccountData, OrderPageData } from '../../../../../api';
import { PriceMode } from '../../../../../common/OrderUtils';

export enum PaymentModel {
  Deposit = 'deposit',
  PayAtVisit = 'pay_at_visit',
  FullPrice = 'full_price',
}

type DepositOrderPage = Pick<OrderPageData, 'deposit_amount_per_person' | 'deposit_currency'>;
type DepositAccount = Pick<AccountData, 'deposit_percentage'> & Partial<Pick<AccountData, 'currency'>>;

// The per-person deposit is in the order page's own currency; one that differs from the account's is ignored, as
// if the order page had none.
export const perPersonDepositOf = (orderPage: DepositOrderPage, account: DepositAccount): number | undefined => {
  const { deposit_currency: depositCurrency } = orderPage;
  if (depositCurrency && account.currency && depositCurrency !== account.currency) {
    return undefined;
  }
  return orderPage.deposit_amount_per_person;
};

// Same rule as the customer app (app/src/orderPage/core/pricing/deposit.ts): a fixed amount per person on the
// order page, else a percentage of the price on the account. Undefined when the experience has no deposit.
export const depositAmountOf = (
  orderPage: DepositOrderPage,
  account: DepositAccount,
  persons: number,
  calculatedPrice: number,
): number | undefined => {
  const perPerson = perPersonDepositOf(orderPage, account);
  if (perPerson != null) {
    return perPerson > 0 ? perPerson * persons : undefined;
  }
  if (account.deposit_percentage && account.deposit_percentage > 0) {
    return Math.round((calculatedPrice * account.deposit_percentage) / 100);
  }
  return undefined;
};

type PaymentModelArgs = {
  orderPage: DepositOrderPage;
  account: DepositAccount;
  priceMode: PriceMode;
  isVoucherRedeem: boolean;
};

export const paymentModelOf = ({ orderPage, account, priceMode, isVoucherRedeem }: PaymentModelArgs): PaymentModel => {
  if (isVoucherRedeem) {
    return PaymentModel.FullPrice;
  }
  const perPerson = perPersonDepositOf(orderPage, account);
  if (priceMode === PriceMode.ZERO || perPerson === 0) {
    return PaymentModel.PayAtVisit;
  }
  if ((perPerson != null && perPerson > 0) || (perPerson == null && (account.deposit_percentage ?? 0) > 0)) {
    return PaymentModel.Deposit;
  }
  return PaymentModel.FullPrice;
};

type DefaultUpfrontArgs = {
  model: PaymentModel;
  deposit: number | undefined;
  orderPrice: number;
};

export const defaultUpfront = ({ model, deposit, orderPrice }: DefaultUpfrontArgs): number => {
  if (model === PaymentModel.Deposit) {
    return deposit ?? 0;
  }
  if (model === PaymentModel.PayAtVisit) {
    return 0;
  }
  return orderPrice;
};

type DepositPriceToSendArgs = {
  model: PaymentModel;
  upfrontAmount: number;
  orderPrice: number;
  createPaymentLink: boolean;
};

export const depositPriceToSend = ({
  model,
  upfrontAmount,
  orderPrice,
  createPaymentLink,
}: DepositPriceToSendArgs): number | undefined => {
  if (model === PaymentModel.Deposit) {
    return upfrontAmount;
  }
  if (createPaymentLink && upfrontAmount > 0 && upfrontAmount < orderPrice) {
    return upfrontAmount;
  }
  return undefined;
};
